import { Progress } from '@backstage/core-components';
import {
  configApiRef,
  fetchApiRef,
  githubAuthApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import { useEffect, useMemo, useState } from 'react';

/**
 * GitHubRepo_OrgTeamsPicker
 *
 * A team dropdown backed by the GitHub API and scoped to BOTH the current user
 * and the organization selected on the first step (`repoUrl` → `owner=<org>`).
 * It lists only the GitHub teams that the signed-in user is actually a member
 * of within that org.
 *
 * How it works:
 *  1. The selected org is read from the sibling `repoUrl` value
 *     (`formContext.formData.repoUrl`).
 *  2. A user-scoped GitHub token is obtained from `githubAuthApi` and sent to
 *     the PDP backend in the `X-GitHub-Token` header.
 *  3. The backend resolves the caller's login from that token (`GET /user`) and
 *     uses the GitHub **App installation** token for the org to list the
 *     caller's teams. The login is never supplied by the client, so a user can
 *     only ever list their own team memberships.
 *
 * Why the lookup is done in the backend: the previous implementation called
 * `GET /user/teams` directly with the user's OAuth token. Under EMU / SAML SSO,
 * that endpoint silently omits teams from any org the token has not been
 * SSO-authorized for — so a user who belonged to teams in two orgs would see
 * teams for one org but "no teams" for the other. The App installation token
 * used by the backend is not subject to per-user SSO, so results are consistent
 * for every org the App is installed on.
 *
 * The stored value is the bare GitHub team slug (e.g. `platform-team`) because
 * it feeds GitHub-native consumers — the `publish:github` admin-access grant
 * (`<org>/<slug>`) and CODEOWNERS (`@<org>/<slug>`) — which expect a team slug,
 * not a catalog entity ref.
 */

type TeamOption = { slug: string; label: string };

/**
 * AD-backed SSO control teams (e.g. `PFG-GITHUB-AD-IS-SSO`,
 * `PFG-GITHUB-AD-IS-SSO-CO`, `PFG-GITHUB-AD-EBS-SSO-CO`). Team slugs are
 * lowercase-hyphenated, so this matches both `pfg-github-ad-<bu>-sso` and
 * `pfg-github-ad-<bu>-sso-co`. These are identity groups and must not be
 * selectable as a Repo Owning Team (GitHub Team).
 */
const SSO_CONTROL_TEAM = /^pfg-github-ad-.*-sso(-co)?$/i;

function parseOwner(repoUrl?: string): string {
  if (!repoUrl) return '';
  try {
    const query = repoUrl.includes('?') ? repoUrl.split('?')[1] : repoUrl;
    return new URLSearchParams(query).get('owner') ?? '';
  } catch {
    return '';
  }
}

export const OrgTeamsPicker = (props: FieldExtensionComponentProps<string>) => {
  const {
    onChange,
    rawErrors,
    required,
    formData,
    schema,
    idSchema,
    formContext,
  } = props;

  const githubAuthApi = useApi(githubAuthApiRef);
  const fetchApi = useApi(fetchApiRef);
  const configApi = useApi(configApiRef);

  const repoUrl = (formContext?.formData as { repoUrl?: string } | undefined)
    ?.repoUrl;
  const org = parseOwner(repoUrl);

  const [teams, setTeams] = useState<TeamOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | undefined>();

  useEffect(() => {
    let active = true;

    if (!org) {
      setTeams([]);
      setLoadError(undefined);
      setLoading(false);
      return () => {
        active = false;
      };
    }

    (async () => {
      setLoading(true);
      setLoadError(undefined);
      try {
        // Obtain a user-scoped GitHub token and let the PDP backend resolve the
        // caller's login from it. The backend never trusts a client-supplied
        // login, so a user can only ever list their own team memberships.
        const token = await githubAuthApi.getAccessToken(['read:org']);

        // Ask the PDP backend (GitHub App installation token) for the teams the
        // caller belongs to in the selected org. The caller's identity is
        // carried by the `X-GitHub-Token` header, not the request body.
        const baseUrl = configApi.getString('backend.baseUrl');
        const res = await fetchApi.fetch(
          `${baseUrl}/api/validation/user-teams`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-GitHub-Token': token,
            },
            body: JSON.stringify({ org }),
          },
        );
        if (!res.ok) {
          // Surface the backend's actual reason (e.g. GitHub App not installed
          // on the org, or missing members:read) instead of a bare status code.
          let detail = '';
          try {
            const body = (await res.json()) as { error?: string };
            detail = body?.error ? `. ${body.error}` : '';
          } catch {
            detail = '';
          }
          throw new Error(`Team lookup returned ${res.status}${detail}`);
        }
        const data = (await res.json()) as {
          teams?: Array<{ slug: string; name?: string }>;
        };

        const options = (data.teams ?? [])
          // Exclude the AD-backed SSO control teams (e.g.
          // PFG-GITHUB-AD-IS-SSO-CO, PFG-GITHUB-AD-EBS-SSO-CO). They are
          // identity/SSO groups, not valid Repo Owning Team (GitHub Team)
          // selections.
          .filter(team => !SSO_CONTROL_TEAM.test(team.slug))
          .map(team => ({ slug: team.slug, label: team.name || team.slug }))
          .sort((a, b) => a.label.localeCompare(b.label));

        if (!active) return;
        setTeams(options);

        // If the previously selected team is not valid for the current org
        // (e.g. the user changed the org on step 1), clear the stale value.
        if (formData && !options.some(o => o.slug === formData)) {
          onChange(undefined);
        }
      } catch (err) {
        if (active) setLoadError((err as Error).message);
      } finally {
        if (active) setLoading(false);
      }
    })();

    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [githubAuthApi, fetchApi, configApi, org]);

  const hasError = rawErrors !== undefined && rawErrors.length > 0 && !formData;
  const noOrg = !org;
  const noTeams = !!org && !loading && !loadError && teams.length === 0;

  const helperText = useMemo(() => {
    if (noOrg)
      return 'Select an organization on the first step to load its GitHub teams.';
    if (loadError) return `Could not load your GitHub teams: ${loadError}`;
    if (noTeams)
      return `You are not a member of any GitHub team in the ${org} organization. Ask a team maintainer to add you, then reopen this form.`;
    return schema?.description;
  }, [noOrg, loadError, noTeams, org, schema?.description]);

  if (loading) {
    return <Progress />;
  }

  return (
    <FormControl
      margin="normal"
      required={required}
      error={hasError || !!loadError}
      fullWidth
    >
      <InputLabel id={`${idSchema?.$id}-label`}>
        {schema?.title ?? 'Team'}
      </InputLabel>
      <Select
        labelId={`${idSchema?.$id}-label`}
        id={idSchema?.$id}
        value={formData ?? ''}
        disabled={noOrg || noTeams || !!loadError}
        onChange={event =>
          onChange((event.target.value as string) || undefined)
        }
      >
        {teams.map(team => (
          <MenuItem key={team.slug} value={team.slug}>
            {team.label}
          </MenuItem>
        ))}
      </Select>
      {helperText && <FormHelperText>{helperText}</FormHelperText>}
    </FormControl>
  );
};

/**
 * Field validation hook point. The Select only ever emits team slugs it
 * rendered from the user's own GitHub memberships, and `required` in the schema
 * enforces a selection, so no async re-check is performed here.
 */
export const validateOrgTeams = () => {
  return;
};
